// eslint-disable-next-line import/named
import { init, replayIntegration, browserTracingIntegration } from "@sentry/remix";
import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import i18next from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import Fetch from "i18next-fetch-backend";
import { startTransition, StrictMode, useEffect, type ErrorInfo } from "react";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { getInitialNamespaces } from "remix-i18next/client";
import { defaultNS, fallbackLng, supportedLngs } from "./i18n";

const ENV = (window as any).ENV as {
	SENTRY_DSN: string | undefined;
	SENTRY_ENVIRONMENT: string | undefined;
	SENTRY_TRACES_SAMPLE_RATE: string | undefined;
	SENTRY_RELEASE: string | undefined;
};
console.log("ENV", ENV);
init({
	dsn: ENV.SENTRY_DSN,
	tracesSampleRate: parseInt(ENV.SENTRY_TRACES_SAMPLE_RATE ?? "1"),
	environment: ENV.SENTRY_ENVIRONMENT ?? "development",
	release: ENV.SENTRY_RELEASE,
	integrations: [browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches
    }), replayIntegration({
        maskAllText: false,
        blockAllMedia: false
    })],

    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
	 beforeSend(event) {
		console.log("Sentry event:", event);
		return event;
	},
	beforeBreadcrumb(breadcrumb) {
		const url = breadcrumb.data?.url;
		if (url) {
			if (url.startsWith("https://www.google-analytics.com")) return null;
			if (url.startsWith("https://c.riskified.com")) return null;
		}
		return breadcrumb;
	}
})

async function main() {
  // eslint-disable-next-line import/no-named-as-default-member
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(Fetch) // Tell i18next to use the Fetch backend
    .use(I18nextBrowserLanguageDetector) // Setup a client-side language detector
    .init({
      defaultNS,
      fallbackLng,
      supportedLngs,
      ns: getInitialNamespaces(),
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ["htmlTag"],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
      backend: {
        // We will configure the backend to fetch the translations from the
        // resource route /api/locales and pass the lng and ns as search params
        loadPath: "/api/locales?lng={{lng}}&ns={{ns}}",
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>
    );
  });
}

main().catch((error) => console.error(error));


// https://medium.com/@craigmorten/how-to-debug-react-hydration-errors-5627f67a6548
// function onRecoverableError(error: Error, errInfo: ErrorInfo) {
// 	const context = {} as {componentStack?: string};
	
// 	 if (errInfo?.componentStack) {
// 		 // Generating this synthetic error allows monitoring services to apply sourcemaps
// 		 // to unminify the stacktrace and make it readable.
// 		 const errorBoundaryError = new Error(error.message);
// 		 errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
// 		 errorBoundaryError.stack = errInfo.componentStack;
	
// 		 // REF: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
// 		 error.cause = errorBoundaryError;
	
// 		 // You can also just add the plain text as added event context.
// 		 context.componentStack = errInfo.componentStack;
// 	 }
	
// 	 // Replace with your error monitoring service.
// 	//MyObservabilityPlatform.captureException(error, { context })
//   }